import { DocumentReference } from "fhir/r5";
import { DocumentCard } from "../../../../../components/common/DocumentCard";
import dayjs from "dayjs";
import { Stack } from "@mui/material";
import { MedicalServices } from "@mui/icons-material";
import { NoDataWarning } from "@/components/common";
import { CertificateActions } from "../../../../ehrrecords/components/patientSummaryAccordions/CertificateDocs/CertificateActions";
import { useFhirData } from "../../../../../utils/useFhirData";

export function ProceduresMobile({ handleOpen, setBinaryUrl, summaryData, setRefetch }: any) {

  // Extrai os dados de procedimentos do sumário
  const solicitationData = useFhirData.extractPatientSummaryResource(
    summaryData,
    "DocumentReference",
    "service-request"
  )?.[0]?.entry;

  const procedureServiceRequests = useFhirData.extractPatientSummaryResource(
    summaryData,
    "ServiceRequest"
  );

  // Filtra apenas documentos de procedimentos
  const procedureDocuments = solicitationData?.filter((doc: any) => 
    doc?.resource?.resourceType === "DocumentReference" && 
    doc?.resource?.category?.[0]?.coding?.[0]?.display === "Procedimento"
  ) || [];

  // Filtra apenas documentos com URL para download
  const procedureDocumentsWithUrl = procedureDocuments?.filter((el: any) => {
    return !!el?.resource?.content?.[0]?.attachment?.url;
  });

  // Mapeia os dados para os cards - seguindo exatamente o mesmo padrão do Procedures.tsx
  const rowData = procedureDocumentsWithUrl
    ?.map((doc: any, index: number) => {
      // Obtém informações do documento
      const binaryUrl = doc?.resource?.content?.[0]?.attachment?.url || "";
      
      // Obter o ServiceRequest associado
      const serviceRequestId = doc?.resource?.basedOn?.[0]?.reference?.split('/')[1];
      let relatedServiceRequest = null;
      
      if (serviceRequestId && procedureServiceRequests?.[0]?.entry) {
        relatedServiceRequest = procedureServiceRequests[0].entry.find((sr: any) => 
          sr?.resource?.id === serviceRequestId
        );
      }
      
      // Obter descrição do procedimento - EXATAMENTE como é feito no ProcedureRequest.tsx
      let description = "";
      
      if (relatedServiceRequest?.resource?.code?.concept?.coding?.[0]?.display) {
        // Pega exatamente do mesmo lugar que o ProcedureRequest.tsx
        description = relatedServiceRequest.resource.code.concept.coding[0].display;
      } else if (doc?.resource?.type?.coding?.[0]?.display) {
        // Se não encontrar no ServiceRequest, pega do tipo do documento
        description = doc.resource.type.coding[0].display;
      } else if (doc?.resource?.title) {
        // Última tentativa: pegar do título do documento
        description = doc.resource.title;
      } else {
        description = "Procedimento";
      }
      
      // Pega o profissional exatamente da mesma forma que o ProcedureRequest
      let practitionerName = "-";
      
      // Primeiro tenta do documento
      if (doc?.resource?.contained) {
        const practitioner = doc.resource.contained.find(
          (cont: any) => cont.resourceType === "Practitioner"
        );
        
        if (practitioner?.name?.[0]?.text) {
          practitionerName = practitioner.name[0].text;
        }
      }
      
      // Se não encontrou, tenta do ServiceRequest
      if (practitionerName === "-" && relatedServiceRequest?.resource?.contained?.[0]?.name?.[0]?.text) {
        practitionerName = relatedServiceRequest.resource.contained[0].name[0].text;
      }
      
      // Formata a data como o ProcedureRequest
      const date = relatedServiceRequest?.resource?.authoredOn 
        ? dayjs(relatedServiceRequest.resource.authoredOn).format("DD/MM/YYYY") 
        : dayjs(doc?.resource?.date).format("DD/MM/YYYY") || "-";
      
      // Obtém o ID do encontro (se disponível)
      const encounter = doc?.resource?.context?.encounter?.reference;
      let encounterId = encounter ? encounter.split("/")?.[1] : undefined;
      
      // Se não houver ServiceRequest, tentar obter ID do encontro do documento
      if (!encounterId && relatedServiceRequest?.resource?.encounter?.reference) {
        const encounterRef = relatedServiceRequest.resource.encounter.reference;
        encounterId = encounterRef.split("/")?.[1];
      }
      
      return {
        id: index,
        description,
        date,
        practitioner: practitionerName,
        binaryUrl,
        encounterId,
        fullResource: doc?.resource,
        fullServiceResource: relatedServiceRequest ? [relatedServiceRequest] : [],
        status: relatedServiceRequest?.resource?.status || "active",
        sign: true, // Adiciona propriedade sign para compatibilidade com CertificateActions
      };
    })
    ?.sort((a: any, b: any) => {
      // Ordena por data decrescente
      const dateA = dayjs(a.date, "DD/MM/YYYY");
      const dateB = dayjs(b.date, "DD/MM/YYYY");
      return dateB.diff(dateA);
    }) || [];

  return (
    <Stack spacing={2}>
      {rowData?.length > 0 ? (
        rowData.map((row: any) => (
          <DocumentCard
            key={row.id}
            title={row.description || "Procedimento"}
            date={row.date}
            practitioner={row.practitioner}
            actions={
              <CertificateActions
                setRefetch={setRefetch}
                row={row}
                handleOpen={handleOpen}
                setBinaryUrl={setBinaryUrl}
                cardActions
              />
            }
          />
        ))
      ) : (
        <NoDataWarning />
      )}
    </Stack>
  );
}
